import React, { useCallback, useRef } from 'react';
import { RadioChangeEvent } from 'antd';
import { useTranslation } from 'react-i18next';

import { ExternalCheckSettingsInput, ProviderModel, RegexCheckSettings } from '../../../../../gamma-api';
import TypedStepModal, {
  TypedStepModalStep,
  TypedStepTypeSelection,
  useTypedStepModalState,
} from '../../../../components/common/TypedStepModal';
import {
  EXTERNAL_MASKING_SCHEMA,
  EXTERNAL_MASKING_SCHEMA_FILE_NAME,
} from '../../../../components/Settings/externalMaskingSchema';
import { useValidationForm } from '../../../../utils/validationUtil';
import { donwloadAsNamedJsonFile } from '../../../../utils/fileUtil';
import {
  createEmptyCheckStep,
  CheckTypeValue,
  getCheckTypeDescription,
  getCheckTypeIconName,
  getCheckTypeTitle,
  ICheckStep,
  GuardrailStageKey,
  normalizeCheckSettings,
} from '../shared/utils';

import CustomPromptCheckParameters from './CheckModalParameters/CustomPromptCheckParameters';
import ExternalCheckParameters from './CheckModalParameters/ExternalCheckParameters';
import RegexCheckParameters from './CheckModalParameters/RegexCheckParameters';
import {
  buildCheckValidationForm,
  checkFieldValueFnMap,
  getFirstInvalidCheckField,
  CheckValidationField,
} from './CheckModal.validation';

const DRAFT_STEP_KEY = '__draft_guardrail__';
const MODAL_WIDTH = 960;

export interface ICheckModalProps {
  isCreation?: boolean;
  providers: ProviderModel[];
  providersLoading: boolean;
  step?: ICheckStep;
  stageKey?: GuardrailStageKey;
  visible: boolean;
  onClose: () => void;
  onSave: (step: ICheckStep) => Promise<void>;
}

const CheckModal: React.FC<ICheckModalProps> = ({
  isCreation = false,
  providers,
  providersLoading,
  step,
  stageKey,
  visible,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const validationForm = useValidationForm(buildCheckValidationForm(step), checkFieldValueFnMap);
  const { draftStep, modalStep, saving, setDraftStep, setModalStep, setSaving } = useTypedStepModalState<ICheckStep>({
    isCreation,
    step,
    visible,
    onReset: (nextStep) => validationForm.reset(buildCheckValidationForm(nextStep)),
  });
  const llmFieldRef = useRef<HTMLDivElement>(null);
  const promptFieldRef = useRef<HTMLDivElement>(null);
  const apiUrlFieldRef = useRef<HTMLDivElement>(null);
  const patternsFieldRef = useRef<HTMLDivElement>(null);
  const customOptionsFieldRef = useRef<HTMLDivElement>(null);

  const validationFieldRefs: Partial<Record<CheckValidationField, React.RefObject<HTMLDivElement>>> = {
    llm: llmFieldRef,
    prompt: promptFieldRef,
    apiUrl: apiUrlFieldRef,
    patterns: patternsFieldRef,
    customOptionsJson: customOptionsFieldRef,
  };

  const onDownloadSchema = useCallback(() => {
    donwloadAsNamedJsonFile(EXTERNAL_MASKING_SCHEMA_FILE_NAME, EXTERNAL_MASKING_SCHEMA);
  }, []);

  const onStepTypeChange = (event: RadioChangeEvent) => {
    const nextStep = createEmptyCheckStep(DRAFT_STEP_KEY, event.target.value as CheckTypeValue);
    setDraftStep(nextStep);
    validationForm.reset(buildCheckValidationForm(nextStep));
  };

  const updateCheckSettings = (patch: Partial<ICheckStep['check']>) => {
    if (!draftStep) {
      return;
    }

    const nextStep = {
      ...draftStep,
      check: normalizeCheckSettings({
        ...draftStep.check,
        ...patch,
      }),
    };

    setDraftStep(nextStep);
    validationForm.updateFormValues(buildCheckValidationForm(nextStep));
  };

  const renderParameters = () => {
    if (!draftStep) {
      return null;
    }

    if (draftStep.check.type === CheckTypeValue.ExternalCheck) {
      return (
        <ExternalCheckParameters
          step={draftStep}
          validationFieldRefs={validationFieldRefs}
          validationForm={validationForm}
          onChange={(patch: Partial<ExternalCheckSettingsInput>) => updateCheckSettings(patch)}
          onDownloadSchema={onDownloadSchema}
        />
      );
    }

    if (draftStep.check.type === CheckTypeValue.RegexCheck) {
      return (
        <RegexCheckParameters
          step={draftStep}
          validationFieldRefs={validationFieldRefs}
          validationForm={validationForm}
          onChange={(patch: Partial<RegexCheckSettings>) => updateCheckSettings(patch)}
        />
      );
    }

    if (draftStep.check.type === CheckTypeValue.CustomPromptCheck) {
      return (
        <CustomPromptCheckParameters
          providers={providers}
          providersLoading={providersLoading}
          stageKey={stageKey}
          step={draftStep}
          validationFieldRefs={validationFieldRefs}
          validationForm={validationForm}
          onChange={updateCheckSettings}
        />
      );
    }

    throw new Error(`Guardrail check type '${draftStep.check.type}' is not supported`);
  };

  const renderTypeSelection = () => (
    <TypedStepTypeSelection
      options={[CheckTypeValue.CustomPromptCheck, CheckTypeValue.ExternalCheck, CheckTypeValue.RegexCheck].map(
        (type) => ({
          description: getCheckTypeDescription(type),
          iconName: getCheckTypeIconName(type),
          title: getCheckTypeTitle(type),
          value: type,
        })
      )}
      value={draftStep?.check.type}
      onChange={onStepTypeChange}
    />
  );

  const onNextClick = () => {
    if (!draftStep) {
      return;
    }

    setModalStep(TypedStepModalStep.Parameters);
  };

  const onSaveClick = async () => {
    if (!draftStep) {
      return;
    }

    if (!validationForm.validate()) {
      const invalidField = getFirstInvalidCheckField(buildCheckValidationForm(draftStep));

      if (invalidField) {
        requestAnimationFrame(() =>
          validationFieldRefs[invalidField]?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        );
      }

      return;
    }

    setSaving(true);

    try {
      await onSave(draftStep);
    } finally {
      setSaving(false);
    }
  };

  const renderTitle = () => {
    if (modalStep === TypedStepModalStep.TypeSelection) {
      return t('Agent: Guardrails: Modal: Add');
    }

    return draftStep ? getCheckTypeTitle(draftStep.check.type) : null;
  };

  return (
    <TypedStepModal
      canGoNext={!!draftStep}
      modalStep={modalStep}
      parameters={renderParameters()}
      saving={saving}
      title={renderTitle()}
      typeSelection={renderTypeSelection()}
      visible={visible}
      width={MODAL_WIDTH}
      onClose={onClose}
      onNext={onNextClick}
      onSave={onSaveClick}
    />
  );
};

export default CheckModal;
